import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Icon, LoadingGroup, LoadingSkeleton, Panel, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import { Pharmacy, useListFavoritePharmaciesQuery } from "@/App/api/pharmacy";
import { getRoundedPharmacyDistance } from "@/App/utils/utils";

const TRANSLATION_PATH = "PHARMACY_TILE";

interface PharmacyTileProps {
  readonly pharmacy: Pharmacy;
  readonly isLoading: boolean;
  readonly button?: JSX.Element;
  readonly userLocation?: {
    readonly latitude: string;
    readonly longitude: string;
  };
  readonly index?: number;
  readonly pharmacyDescription?: string;
  readonly hideDisplayTags?: boolean;
}

export const PharmacyTile: FC<PharmacyTileProps> = ({ pharmacy, isLoading, button, userLocation, pharmacyDescription, hideDisplayTags, index }) => {
  const { t: translate } = useTranslation();
  const { data: favoritePharmacies } = useListFavoritePharmaciesQuery();

  const isFavoritePharmacy = useMemo(() => {
    return (favoritePharmacies?.results || []).map((p) => p.pharmacy.provider_id).includes(pharmacy.provider_id);
  }, [favoritePharmacies, pharmacy]);

  const pharmacyDistance = useMemo(() => {
    if (pharmacyDescription) return;

    return getRoundedPharmacyDistance(pharmacy);
  }, [pharmacy, pharmacyDescription]);

  const uncompressedView = useMemo(() => {
    return (
      <Shelf>
        <Stack spacing="mini">
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 36 }} />}>
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <Subheading testId={`pharmacy-${pharmacy.provider_id}-name`}>
                  {index != null && `${index + 1}. `}
                  {pharmacy?.name}
                </Subheading>
                {isFavoritePharmacy && <Icon testId={`pharmacy-${pharmacy.provider_id}-favorite-pharmacy`} name="star-filled" />}
                <Shelf.Spacer />
              </Shelf>
              {!hideDisplayTags && (
                <Shelf>
                  {pharmacy.pharmacy_display_rules?.display_tag && (
                    <Tag testId={`pharmacy-${pharmacy.provider_id}-tag`} style={{ backgroundColor: "#313e48", color: "#f0f3f7" }}>
                      {pharmacy.pharmacy_display_rules.display_tag}
                    </Tag>
                  )}
                  <Shelf.Spacer />
                </Shelf>
              )}
            </Stack>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%", height: 24 }} />}>
            {!!pharmacyDescription && (
              <Body>{pharmacyDescription}</Body>
            )}
            {!pharmacyDescription && (
              <>
                {!!pharmacyDistance && (
                  <Body>{pharmacyDistance} {translate(`${TRANSLATION_PATH}.miles`, { count: pharmacy?.distance })}</Body>
                )}
                {(!!pharmacy?.address_line_1 || !!pharmacy?.address_line_2) && (
                  <Body>
                    {pharmacy?.address_line_1 || ""} {pharmacy?.address_line_2 || ""}
                  </Body>
                )}
              </>
            )}
          </LoadingGroup.Placeholder>
        </Stack>
        {!!button && <Shelf.Spacer />}
        {!!button && button}
      </Shelf>
    );
  }, [isFavoritePharmacy, pharmacyDistance, pharmacy, isLoading, index, button, userLocation, pharmacyDescription]);

  const compressedView = useMemo(() => {
    return (
      <Stack>
        <Stack spacing="mini">
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: 120, height: 36 }} />}>
            <Shelf spacing="mini">
              <Subheading testId={`pharmacy-${pharmacy.provider_id}-name`}>
                {index != null && `${index + 1}. `}
                {pharmacy?.name}
              </Subheading>
              {isFavoritePharmacy && <Icon testId={`pharmacy-${pharmacy.provider_id}-favorite-pharmacy`} name="star-filled" />}
              <Shelf.Spacer />
            </Shelf>
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%", height: 24 }} />}>
            {!hideDisplayTags && (
              <Shelf>
                {pharmacy.pharmacy_display_rules?.display_tag && (
                  <Tag testId={`pharmacy-${pharmacy.provider_id}-tag`} style={{ backgroundColor: "#313e48", color: "#f0f3f7" }}>
                    {pharmacy.pharmacy_display_rules.display_tag}
                  </Tag>
                )}
                <Shelf.Spacer />
              </Shelf>
            )}
          </LoadingGroup.Placeholder>
          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ width: "100%", height: 24 }} />}>
            {!!pharmacyDescription && (
              <Body>{pharmacyDescription}</Body>
            )}
            {!pharmacyDescription && (
              <>
                {!!pharmacyDistance && (
                  <Body>{pharmacyDistance} {translate(`${TRANSLATION_PATH}.miles`, { count: pharmacy?.distance })}</Body>
                )}
                {(!!pharmacy?.address_line_1 || !!pharmacy?.address_line_2) && (
                  <Body>
                    {pharmacy?.address_line_1 || ""} {pharmacy?.address_line_2 || ""}
                  </Body>
                )}
              </>
            )}
          </LoadingGroup.Placeholder>
        </Stack>
        {!!button && button}
      </Stack>
    );
  }, [isFavoritePharmacy, pharmacy, isLoading, index, button, userLocation]);

  const { component } = useResponsive({
    component: {
      mobile: compressedView,
      desktop: uncompressedView,
    },
  });

  return (
    <LoadingGroup loading={isLoading}>
      <Panel appearance="primary" className="member-portal-panel">{component}</Panel>
    </LoadingGroup>
  );
};
